import Adjust from "@adjustcom/adjust-web-sdk";

// Type definitions for Adjust event parameters
interface AdjustKeyValue {
  key: string;
  value: string;
}

interface AdjustEventOptions {
  deduplicationId?: string;
  callbackParams?: AdjustKeyValue[];
  partnerParams?: AdjustKeyValue[];
}

// Environment variables for Adjust configuration
const ADJUST_CONFIG = {
  appToken: import.meta.env.VITE_ADJUST_APP_TOKEN || "dummy_app_token",
  environment: import.meta.env.MODE === "production" ? "production" : "sandbox",
  logLevel: import.meta.env.MODE === "production" ? "error" : "verbose",
} as const;

// Event tokens - will be replaced with real tokens from Adjust dashboard
const ADJUST_EVENT_TOKENS = {
  ONBOARDING_START:
    import.meta.env.VITE_ADJUST_EVENT_ONBOARDING_START ||
    "dummy_onboarding_start",
  ONBOARDING_COMPLETED:
    import.meta.env.VITE_ADJUST_EVENT_ONBOARDING_COMPLETED ||
    "dummy_onboarding_completed",
  SIGNUP_START:
    import.meta.env.VITE_ADJUST_EVENT_SIGNUP_START || "dummy_signup_start",
  SIGNUP_COMPLETE:
    import.meta.env.VITE_ADJUST_EVENT_SIGNUP_COMPLETE ||
    "dummy_signup_complete",
} as const;

// Track if Adjust has been initialized
let isAdjustInitialized = false;

/**
 * Initialize the Adjust SDK
 * Should be called once when the app starts, client-side only
 */
function initAdjust(): void {
  // SSR safety check
  if (typeof window === "undefined") {
    console.warn("[Adjust] Cannot initialize on server-side");
    return;
  }

  // Prevent double initialization
  if (isAdjustInitialized) {
    console.warn("[Adjust] SDK already initialized");
    return;
  }

  try {
    Adjust.initSdk({
      appToken: ADJUST_CONFIG.appToken,
      environment: ADJUST_CONFIG.environment as "production" | "sandbox",
      logLevel: ADJUST_CONFIG.logLevel as
        | "none"
        | "error"
        | "warning"
        | "info"
        | "verbose",
    });

    isAdjustInitialized = true;
  } catch (error) {
    console.error("[Adjust] Failed to initialize SDK:", error);
  }
}

/**
 * Track an Adjust event
 * @param eventToken - The event token from Adjust dashboard
 * @param options - Optional parameters for the event
 */
function trackAdjustEvent(
  eventToken: string,
  options?: AdjustEventOptions
): void {
  // SSR safety check
  if (typeof window === "undefined") {
    console.warn("[Adjust] Cannot track events on server-side");
    return;
  }

  // Ensure SDK is initialized
  if (!isAdjustInitialized) {
    console.warn(
      "[Adjust] SDK not initialized. Call initAdjust() first. Attempting to initialize now..."
    );
    initAdjust();
  }

  try {
    Adjust.trackEvent({
      eventToken,
      deduplicationId: options?.deduplicationId,
      callbackParams: options?.callbackParams,
      partnerParams: options?.partnerParams,
    });
  } catch (error) {
    console.error(`[Adjust] Failed to track event ${eventToken}:`, error);
  }
}

/**
 * Track onboarding start event
 * Called when user first enters the onboarding flow
 */
function trackOnboardingStart(language?: string): void {
  const callbackParams: AdjustKeyValue[] = [];

  if (language) {
    callbackParams.push({ key: "language", value: language });
  }

  trackAdjustEvent(ADJUST_EVENT_TOKENS.ONBOARDING_START, {
    callbackParams: callbackParams.length > 0 ? callbackParams : undefined,
  });
}

/**
 * Track onboarding completed event
 * Called when user completes the entire onboarding flow
 */
function trackOnboardingCompleted(): void {
  trackAdjustEvent(ADJUST_EVENT_TOKENS.ONBOARDING_COMPLETED);
}

/**
 * Track signup start event
 * Called when user initiates the signup process (e.g., clicks "Create account")
 */
function trackSignupStart(method?: string): void {
  const callbackParams: AdjustKeyValue[] = [];

  if (method) {
    callbackParams.push({ key: "method", value: method });
  }

  trackAdjustEvent(ADJUST_EVENT_TOKENS.SIGNUP_START, {
    callbackParams: callbackParams.length > 0 ? callbackParams : undefined,
  });
}

/**
 * Track signup complete event
 * Called when user successfully completes signup
 * Uses deduplication to prevent duplicate events
 */
function trackSignupComplete(userId: string, email?: string): void {
  const callbackParams: AdjustKeyValue[] = [{ key: "user_id", value: userId }];

  if (email) {
    callbackParams.push({ key: "email", value: email });
  }

  trackAdjustEvent(ADJUST_EVENT_TOKENS.SIGNUP_COMPLETE, {
    deduplicationId: `signup_complete:${userId}`,
    callbackParams,
  });
}

// Export all functions
export {
  initAdjust,
  trackAdjustEvent,
  trackOnboardingStart,
  trackOnboardingCompleted,
  trackSignupStart,
  trackSignupComplete,
  ADJUST_EVENT_TOKENS,
};
